// src/annihilation/BeingSprite.js
// ============================================================
//  ANNIHILATION — BEING VISUALS (one source of truth)
//
//  "What does a Spectral Tiger LOOK like on screen?" is answered
//  HERE and nowhere else. The Plaza, the Grove, the Sunken Fold,
//  Sigfer's Yard, and every future zone render the player through
//  this file — so the being you Transcended into is the being you
//  see, in every room, always. (July 16 continuity law.)
//
//  THREE EXPORTS:
//
//  · BeingSprite            — the player AS their being. Generated
//                             procedural art per the Batch 4.5
//                             doctrine ("generate if you have to").
//                             Real CDN art auto-covers it the day
//                             `beings/<beingId>.webp` lands — same
//                             onLoad gate as the Old Fold ground.
//
//  · TerrorTravelerVisual   — THE REVERSAL. In Terror you are the
//                             human and the beast walks beside you.
//                             In Annihilation you ARE the beast —
//                             and the small human trailing you is
//                             your Terror character, card in hand.
//                             Placeholder for the cross-game import
//                             (configured later); it first tries
//                             the real Terror player art, then
//                             falls back to the generated traveler.
//
//  · AnnihilationPlayer     — the overseer wrapper every zone
//                             mounts instead of raw PlayerEntity.
//                             ONE mount shape; positioning math
//                             stays in engine/PlayerEntity (never
//                             re-derived here); this file only
//                             decides WHAT fills the frame.
//
//  Vyren pattern: being ids come straight from beings.js and are
//  never restated here as literals in logic — only as registry
//  keys for the visual recipes. Colors/shapes are display-only
//  and free to retune.
//
//  THE ANIMATION PASS (July 19) — this is the justified reopen
//  the header promised. Each being now has a STRIKE POSTURE:
//  the body itself moves when a Lyt swing fires (tiger lunges,
//  dragon rears and snaps, Z4Z pivots the blade, Xulan surges).
//  Postures live HERE (this file owns "what the being looks
//  like"); the strike's projected visual (rakes, jaws, arcs,
//  lance) lives in AbilityFX.js — one registry, keyed by the
//  same internal ability ids. Zones pass ctrl.lastSwing through
//  AnnihilationPlayer's `swing` prop; a new id re-triggers the
//  posture. Real CDN art lunges too — the wrapper animates
//  whatever fills the frame.
// ============================================================

import { useState, useEffect, useRef } from "react";
import { CDN } from "./CDN";
import { getPlayerSprite, getIdleSprite } from "./SpriteManifest";
import { PlayerEntity } from "./PlayerEntity";
import { levelForXp, tierForLevel, TIERS } from "./stats";

// ── Keyframes — imperative, id-guarded (house law: no JSX <style>) ──
if (typeof document !== "undefined" && !document.getElementById("anni-being-kf")) {
  const el = document.createElement("style");
  el.id = "anni-being-kf";
  el.textContent = `
@keyframes anniPhase   { 0%,100%{opacity:0.92; filter:none} 50%{opacity:0.72; filter:blur(0.4px)} }
@keyframes anniAura    { 0%,100%{opacity:0.5; transform:scale(0.97)} 50%{opacity:0.9; transform:scale(1.05)} }
@keyframes anniEmber   { 0%{transform:translateY(0); opacity:0.9} 100%{transform:translateY(-14px); opacity:0} }
@keyframes anniGear    { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
@keyframes anniLevi    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6%)} }
@keyframes anniBreathe { 0%,100%{transform:scaleY(1)} 50%{transform:scaleY(1.03)} }
@keyframes anniCard    { 0%,100%{transform:rotate(-8deg) translateY(0)} 50%{transform:rotate(-8deg) translateY(-2px)} }
@keyframes anniStrikeLunge { 0%{transform:translateX(0)} 35%{transform:translateX(14%) scaleX(1.04)} 100%{transform:translateX(0)} }
@keyframes anniStrikeSnap  { 0%{transform:rotate(0deg) translateY(0)} 30%{transform:rotate(-9deg) translateY(-5%)} 62%{transform:rotate(7deg) translateY(2%)} 100%{transform:rotate(0deg) translateY(0)} }
@keyframes anniStrikeTwist { 0%{transform:rotate(0deg)} 30%{transform:rotate(-14deg)} 65%{transform:rotate(12deg)} 100%{transform:rotate(0deg)} }
@keyframes anniStrikeCast  { 0%{transform:scale(1); filter:brightness(1)} 40%{transform:scale(1.08); filter:brightness(1.6)} 100%{transform:scale(1); filter:brightness(1)} }
`;
  document.head.appendChild(el);
}

// ── Per-being palette (display strings — retune freely) ──────
const BEING_PALETTE = {
  spectral_tiger: { main: "#7fe8d8", deep: "#0f2b28", glow: "#b8fff2", accent: "#dffcf6" },
  chaos_dragon:   { main: "#e84a3a", deep: "#4a120c", glow: "#ff9b2e", accent: "#ffd08a" },
  z4z:            { main: "#d8b24a", deep: "#3a3f4c", glow: "#4ae8ff", accent: "#8a93a8" },
  xulan:          { main: "#7c5cff", deep: "#1c1038", glow: "#c9b8ff", accent: "#a88cff" },
};
export const getBeingPalette = (beingId) =>
  BEING_PALETTE[beingId] || { main: "#c9a84c", deep: "#241c12", glow: "#f0d060", accent: "#e8d8a8" };

// ── Tier accents — the Ascent, visible ────────────────────────
// Beb: the base body. Champion: an aura ring. Lord: crown + brighter glow.
function TierAccents({ tier, glow }) {
  if (!tier || tier === TIERS.BEB) return null;
  const lord = tier === TIERS.LORD;
  return (
    <>
      {/* aura ring — Champion and up */}
      <div style={{
        position: "absolute", inset: lord ? "-6%" : "0%",
        borderRadius: "50%",
        border: `1px solid ${glow}${lord ? "aa" : "55"}`,
        boxShadow: `0 0 ${lord ? 18 : 9}px ${glow}${lord ? "77" : "33"}, inset 0 0 ${lord ? 12 : 6}px ${glow}22`,
        animation: "anniAura 2.8s ease-in-out infinite",
        pointerEvents: "none",
      }} />
      {/* crown — Lord only. Three spikes above the head. */}
      {lord && (
        <div style={{ position: "absolute", top: "-12%", left: "50%", transform: "translateX(-50%)",
          display: "flex", gap: "6%", alignItems: "flex-end", pointerEvents: "none" }}>
          {[0.6, 1, 0.6].map((h, i) => (
            <div key={i} style={{
              width: 4, height: 10 * h,
              background: `linear-gradient(180deg, ${glow}, ${glow}66)`,
              clipPath: "polygon(50% 0, 100% 100%, 0 100%)",
              filter: `drop-shadow(0 0 3px ${glow})`,
            }} />
          ))}
        </div>
      )}
    </>
  );
}

// ── Shared ground shadow ──────────────────────────────────────
const GroundShadow = ({ soft }) => (
  <div style={{ position: "absolute", bottom: 0, left: "18%", width: "64%", height: "9%",
    borderRadius: "50%", background: `rgba(0,0,0,${soft ? 0.3 : 0.45})`, filter: "blur(1.5px)" }} />
);

// ─────────────────────────────────────────────────────────────
// THE FOUR BODIES — pure CSS, zero assets, deterministic.
// Each fills its parent (width/height 100%), faces RIGHT by
// default; BeingSprite mirrors the whole body for left-facing.
// walkFrame bobs the body; isIdle breathes it.
// ─────────────────────────────────────────────────────────────

// SAEL'ESH — the Spectral Tiger. A low prowler between two worlds:
// ghost-teal, dark stripes, glowing eyes, phase shimmer.
function SpectralTigerBody({ p, walkFrame, isIdle }) {
  const bob = walkFrame ? "-3%" : "0%";
  return (
    <div style={{ position: "absolute", inset: 0,
      transform: `translateY(${bob})`,
      transition: "transform 0.12s",
      animation: isIdle ? "anniBreathe 3s ease-in-out infinite" : "anniPhase 2.6s ease-in-out infinite",
      transformOrigin: "50% 100%" }}>
      {/* phantom aura */}
      <div style={{ position: "absolute", inset: "6% 0 4% 0", borderRadius: "50%",
        background: `radial-gradient(ellipse at 50% 60%, ${p.main}33 0%, transparent 70%)`,
        filter: "blur(3px)", animation: "anniAura 3.4s ease-in-out infinite" }} />
      {/* tail — curved, dark tip */}
      <div style={{ position: "absolute", left: "2%", top: "34%", width: "26%", height: "10%",
        borderRadius: "50% 40% 40% 50%",
        background: `linear-gradient(90deg, ${p.deep}, ${p.main}cc 55%)`,
        transform: "rotate(-24deg)", transformOrigin: "100% 50%" }} />
      {/* body — long low torso */}
      <div style={{ position: "absolute", left: "16%", top: "38%", width: "56%", height: "34%",
        borderRadius: "46% 40% 34% 44%",
        background: `linear-gradient(180deg, ${p.main}ee 0%, ${p.main}aa 60%, ${p.main}66 100%)`,
        boxShadow: `inset -3px -4px 8px rgba(0,0,0,0.35), 0 0 10px ${p.main}44` }}>
        {/* stripes */}
        {[24, 46, 68].map((l, i) => (
          <div key={i} style={{ position: "absolute", left: `${l}%`, top: "6%",
            width: "9%", height: `${58 - i * 8}%`, borderRadius: "0 0 60% 60%",
            background: p.deep, opacity: 0.75, transform: `rotate(${i % 2 ? 4 : -3}deg)` }} />
        ))}
      </div>
      {/* legs — four, staggered by walkFrame */}
      {[
        { l: "20%", front: false }, { l: "32%", front: false },
        { l: "56%", front: true },  { l: "66%", front: true },
      ].map((leg, i) => (
        <div key={i} style={{ position: "absolute", left: leg.l, top: "64%",
          width: "8%", height: "28%", borderRadius: "30% 30% 40% 40%",
          background: `linear-gradient(180deg, ${p.main}bb, ${p.main}55)`,
          transform: walkFrame === (i % 2 === 0) ? "rotate(8deg)" : "rotate(-6deg)",
          transformOrigin: "50% 0%", transition: "transform 0.12s" }}>
          {/* the claws — the only part on your side of the veil */}
          <div style={{ position: "absolute", bottom: 0, left: "8%", width: "84%", height: "18%",
            borderRadius: "0 0 40% 40%", background: p.accent, boxShadow: `0 0 4px ${p.glow}` }} />
        </div>
      ))}
      {/* head — right end, ears, glowing eyes */}
      <div style={{ position: "absolute", left: "64%", top: "22%", width: "28%", height: "28%",
        borderRadius: "48% 52% 46% 44%",
        background: `linear-gradient(150deg, ${p.main}f2, ${p.main}99)`,
        boxShadow: `inset -2px -3px 6px rgba(0,0,0,0.3), 0 0 8px ${p.main}55` }}>
        {/* ears */}
        <div style={{ position: "absolute", top: "-26%", left: "10%", width: "26%", height: "40%",
          clipPath: "polygon(50% 0, 100% 100%, 0 100%)", background: p.main }} />
        <div style={{ position: "absolute", top: "-26%", right: "14%", width: "26%", height: "40%",
          clipPath: "polygon(50% 0, 100% 100%, 0 100%)", background: p.main }} />
        {/* muzzle stripe */}
        <div style={{ position: "absolute", top: "12%", left: "40%", width: "10%", height: "40%",
          borderRadius: "0 0 60% 60%", background: p.deep, opacity: 0.7 }} />
        {/* eyes — they glow through the veil */}
        <div style={{ position: "absolute", top: "38%", left: "22%", width: "14%", height: "12%",
          minWidth: 2, minHeight: 2, borderRadius: "50%",
          background: p.glow, boxShadow: `0 0 5px ${p.glow}` }} />
        <div style={{ position: "absolute", top: "38%", right: "18%", width: "14%", height: "12%",
          minWidth: 2, minHeight: 2, borderRadius: "50%",
          background: p.glow, boxShadow: `0 0 5px ${p.glow}` }} />
      </div>
      <GroundShadow soft />
    </div>
  );
}

// XERATH'OUN — the Chaos Dragon. Hulking crimson, horns, a folded
// wing, spade tail, embers rising off the hide.
function ChaosDragonBody({ p, walkFrame, isIdle }) {
  const bob = walkFrame ? "-2.5%" : "0%";
  return (
    <div style={{ position: "absolute", inset: 0,
      transform: `translateY(${bob})`, transition: "transform 0.12s",
      animation: isIdle ? "anniBreathe 2.6s ease-in-out infinite" : "none",
      transformOrigin: "50% 100%" }}>
      {/* embers */}
      {[26, 48, 66].map((l, i) => (
        <div key={i} style={{ position: "absolute", left: `${l}%`, top: "34%",
          width: 3, height: 3, borderRadius: "50%", background: p.glow,
          boxShadow: `0 0 4px ${p.glow}`,
          animation: `anniEmber ${1.6 + i * 0.5}s linear infinite`,
          animationDelay: `${i * 0.4}s` }} />
      ))}
      {/* spade tail */}
      <div style={{ position: "absolute", left: "0%", top: "44%", width: "24%", height: "9%",
        borderRadius: "50%", background: `linear-gradient(90deg, ${p.deep}, ${p.main})`,
        transform: "rotate(-18deg)", transformOrigin: "100% 50%" }}>
        <div style={{ position: "absolute", left: "-14%", top: "-40%", width: "34%", height: "180%",
          clipPath: "polygon(0 50%, 100% 0, 70% 50%, 100% 100%)", background: p.deep }} />
      </div>
      {/* folded wing — behind the torso */}
      <div style={{ position: "absolute", left: "24%", top: "10%", width: "34%", height: "40%",
        clipPath: "polygon(10% 100%, 30% 0, 100% 30%, 78% 100%)",
        background: `linear-gradient(160deg, ${p.deep} 20%, ${p.main}88 100%)`,
        boxShadow: `0 0 8px ${p.deep}` }} />
      {/* torso — heavy */}
      <div style={{ position: "absolute", left: "20%", top: "34%", width: "50%", height: "40%",
        borderRadius: "44% 40% 38% 42%",
        background: `linear-gradient(180deg, ${p.main} 0%, ${p.deep} 100%)`,
        boxShadow: `inset -4px -5px 10px rgba(0,0,0,0.45), 0 0 10px ${p.deep}88` }}>
        {/* belly plates */}
        <div style={{ position: "absolute", bottom: "8%", left: "18%", width: "64%", height: "34%",
          borderRadius: "40%", background: `repeating-linear-gradient(90deg, ${p.accent}55 0 6px, ${p.accent}22 6px 12px)`,
          opacity: 0.8 }} />
      </div>
      {/* legs — thick */}
      {[{ l: "26%" }, { l: "56%" }].map((leg, i) => (
        <div key={i} style={{ position: "absolute", left: leg.l, top: "66%",
          width: "13%", height: "26%", borderRadius: "26% 26% 40% 40%",
          background: `linear-gradient(180deg, ${p.main}dd, ${p.deep})`,
          transform: walkFrame === (i === 0) ? "rotate(7deg)" : "rotate(-5deg)",
          transformOrigin: "50% 0%", transition: "transform 0.12s" }} />
      ))}
      {/* head — horned, toothy */}
      <div style={{ position: "absolute", left: "60%", top: "14%", width: "32%", height: "28%",
        borderRadius: "42% 56% 50% 40%",
        background: `linear-gradient(150deg, ${p.main}, ${p.deep})`,
        boxShadow: "inset -3px -3px 7px rgba(0,0,0,0.4)" }}>
        {/* horns */}
        <div style={{ position: "absolute", top: "-38%", left: "6%", width: "18%", height: "56%",
          clipPath: "polygon(50% 0, 100% 100%, 0 100%)", background: p.accent,
          transform: "rotate(-16deg)" }} />
        <div style={{ position: "absolute", top: "-30%", left: "34%", width: "16%", height: "46%",
          clipPath: "polygon(50% 0, 100% 100%, 0 100%)", background: p.accent,
          transform: "rotate(-6deg)" }} />
        {/* eye */}
        <div style={{ position: "absolute", top: "34%", left: "44%", width: "13%", height: "13%",
          minWidth: 2, minHeight: 2, borderRadius: "50%",
          background: p.glow, boxShadow: `0 0 6px ${p.glow}` }} />
        {/* teeth line along the snout */}
        <div style={{ position: "absolute", bottom: "18%", right: "4%", width: "56%", height: "12%",
          background: `repeating-linear-gradient(90deg, #fff8 0 3px, transparent 3px 7px)`,
          clipPath: "polygon(0 0, 100% 0, 96% 100%, 4% 100%)" }} />
      </div>
      <GroundShadow />
    </div>
  );
}

// QUAN-7 — Z4Z. The clockwork knight: angular chassis, cyan visor,
// antenna, pauldrons, a gear turning in the chest.
function Z4ZBody({ p, walkFrame, isIdle }) {
  const bob = walkFrame ? "-2%" : "0%";
  return (
    <div style={{ position: "absolute", inset: 0,
      transform: `translateY(${bob})`, transition: "transform 0.1s",
      transformOrigin: "50% 100%" }}>
      {/* antenna */}
      <div style={{ position: "absolute", left: "49%", top: "-4%", width: 2, height: "16%",
        background: p.accent }}>
        <div style={{ position: "absolute", top: -3, left: -2, width: 5, height: 5, minWidth: 3, minHeight: 3,
          borderRadius: "50%", background: p.glow, boxShadow: `0 0 5px ${p.glow}`,
          animation: "anniAura 1.6s ease-in-out infinite" }} />
      </div>
      {/* head — visor block */}
      <div style={{ position: "absolute", left: "34%", top: "10%", width: "32%", height: "20%",
        clipPath: "polygon(8% 0, 92% 0, 100% 70%, 88% 100%, 12% 100%, 0 70%)",
        background: `linear-gradient(180deg, ${p.accent}, ${p.deep})`,
        boxShadow: "inset -2px -2px 4px rgba(0,0,0,0.5)" }}>
        {/* visor slit */}
        <div style={{ position: "absolute", top: "42%", left: "14%", width: "72%", height: "18%",
          minHeight: 2, background: p.glow, boxShadow: `0 0 6px ${p.glow}`,
          animation: isIdle ? "anniAura 2.2s ease-in-out infinite" : "none" }} />
      </div>
      {/* pauldrons */}
      <div style={{ position: "absolute", left: "16%", top: "30%", width: "18%", height: "12%",
        clipPath: "polygon(0 100%, 20% 0, 100% 0, 100% 100%)", background: p.main,
        boxShadow: "inset -2px -2px 3px rgba(0,0,0,0.4)" }} />
      <div style={{ position: "absolute", right: "16%", top: "30%", width: "18%", height: "12%",
        clipPath: "polygon(0 0, 80% 0, 100% 100%, 0 100%)", background: p.main,
        boxShadow: "inset -2px -2px 3px rgba(0,0,0,0.4)" }} />
      {/* chassis */}
      <div style={{ position: "absolute", left: "28%", top: "32%", width: "44%", height: "36%",
        clipPath: "polygon(12% 0, 88% 0, 100% 24%, 88% 100%, 12% 100%, 0 24%)",
        background: `linear-gradient(180deg, ${p.deep} 0%, #232733 100%)`,
        boxShadow: `inset -3px -3px 6px rgba(0,0,0,0.55), 0 0 8px ${p.glow}22` }}>
        {/* the chest gear — time turning */}
        <div style={{ position: "absolute", top: "26%", left: "50%", marginLeft: "-19%",
          width: "38%", height: "48%",
          borderRadius: "50%", border: `2px dashed ${p.main}`,
          background: `radial-gradient(circle, ${p.main}44 0%, transparent 70%)`,
          animation: "anniGear 6s linear infinite" }}>
          <div style={{ position: "absolute", inset: "30%", borderRadius: "50%",
            background: p.glow, boxShadow: `0 0 6px ${p.glow}` }} />
        </div>
      </div>
      {/* legs — stiff struts */}
      {[{ l: "34%" }, { l: "54%" }].map((leg, i) => (
        <div key={i} style={{ position: "absolute", left: leg.l, top: "66%",
          width: "11%", height: "28%",
          clipPath: "polygon(20% 0, 80% 0, 100% 100%, 0 100%)",
          background: `linear-gradient(180deg, ${p.accent}, ${p.deep})`,
          transform: walkFrame === (i === 0) ? "rotate(6deg)" : "rotate(-5deg)",
          transformOrigin: "50% 0%", transition: "transform 0.1s" }} />
      ))}
      <GroundShadow />
    </div>
  );
}

// OS'ORUN-VETH — Xulan. The borrowed vessel: a hooded mystic that
// floats, robe tapering to nothing, one eye glowing in the hood.
function XulanBody({ p, isIdle }) {
  return (
    <div style={{ position: "absolute", inset: 0,
      animation: "anniLevi 2.8s ease-in-out infinite" }}>
      {/* levitation ring on the ground */}
      <div style={{ position: "absolute", bottom: "1%", left: "24%", width: "52%", height: "8%",
        borderRadius: "50%", border: `1px solid ${p.main}55`,
        boxShadow: `0 0 8px ${p.main}44`, animation: "anniAura 2.8s ease-in-out infinite" }} />
      {/* robe — tapers to a point, no legs */}
      <div style={{ position: "absolute", left: "26%", top: "26%", width: "48%", height: "62%",
        clipPath: "polygon(14% 0, 86% 0, 100% 34%, 66% 100%, 50% 84%, 34% 100%, 0 34%)",
        background: `linear-gradient(180deg, ${p.main}dd 0%, ${p.deep} 90%)`,
        boxShadow: `inset -4px -4px 10px rgba(0,0,0,0.5), 0 0 12px ${p.main}33` }}>
        {/* sigil line down the robe */}
        <div style={{ position: "absolute", top: "20%", left: "48%", width: "4%", height: "56%",
          minWidth: 1, background: `linear-gradient(180deg, ${p.glow}aa, transparent)` }} />
      </div>
      {/* sleeves — folded arms */}
      <div style={{ position: "absolute", left: "20%", top: "40%", width: "60%", height: "13%",
        borderRadius: "40%", background: `linear-gradient(180deg, ${p.main}bb, ${p.deep})`,
        boxShadow: "inset -2px -3px 5px rgba(0,0,0,0.45)" }} />
      {/* hood */}
      <div style={{ position: "absolute", left: "32%", top: "6%", width: "36%", height: "28%",
        borderRadius: "50% 50% 42% 42%",
        background: `linear-gradient(180deg, ${p.main} 0%, ${p.deep} 78%)`,
        boxShadow: `inset -3px -3px 6px rgba(0,0,0,0.5)` }}>
        {/* the shadowed face — nothing in it but the eye */}
        <div style={{ position: "absolute", inset: "34% 18% 12% 18%", borderRadius: "50%",
          background: "#08040f" }}>
          <div style={{ position: "absolute", top: "30%", left: "50%", marginLeft: "-9%",
            width: "18%", height: "26%",
            minWidth: 2, minHeight: 2, borderRadius: "50%", background: p.glow,
            boxShadow: `0 0 7px ${p.glow}`,
            animation: isIdle ? "anniAura 3.2s ease-in-out infinite" : "none" }} />
        </div>
      </div>
      <GroundShadow soft />
    </div>
  );
}

const BEING_BODIES = {
  spectral_tiger: SpectralTigerBody,
  chaos_dragon: ChaosDragonBody,
  z4z: Z4ZBody,
  xulan: XulanBody,
};

// ── STRIKE POSTURES — how each body throws a Lyt swing ────────
// Display strings only (retune freely); ids come from beings.js.
// +x is forward (the body faces right; mirroring wraps outside),
// so the tiger's lunge always travels the way it's facing.
const STRIKE_ANIMS = {
  spectral_tiger: "anniStrikeLunge 0.24s ease-out", //  the pounce
  chaos_dragon: "anniStrikeSnap 0.3s ease-in-out", //   rear + snap
  z4z: "anniStrikeTwist 0.26s ease-in-out", //          the blade pivot
  xulan: "anniStrikeCast 0.28s ease-out", //            the psychic surge
};

// ─────────────────────────────────────────────────────────────
// BEING SPRITE — the player as their being.
//
// Fills its parent (the engine's positioned player div). Faces
// RIGHT by default; facing.x === -1 mirrors the whole body.
// Attempts real CDN art (`beings/<beingId>.webp`) first — the
// generated body renders until that art actually LOADS (onLoad
// gate, July 15 white-void doctrine: never trust URL truthiness).
// ─────────────────────────────────────────────────────────────
export function BeingSprite({ beingId, facing = { x: 0, y: 1 }, walkFrame = false, isIdle = false, tier = null, swing = null }) {
  const p = getBeingPalette(beingId);
  const Body = BEING_BODIES[beingId] || SpectralTigerBody;
  const strikeAnim = STRIKE_ANIMS[beingId] || STRIKE_ANIMS.spectral_tiger;

  const artSrc = beingId ? `${CDN}/beings/${beingId}.webp` : null;
  const [artLive, setArtLive] = useState(false);
  const [artDead, setArtDead] = useState(false);
  const prevSrc = useRef(artSrc);
  useEffect(() => {
    if (prevSrc.current !== artSrc) { prevSrc.current = artSrc; setArtLive(false); setArtDead(false); }
  }, [artSrc]);

  const mirror = (facing?.x || 0) < 0;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%",
      transform: mirror ? "scaleX(-1)" : "none" }}>
      {/* the strike wrapper — a new swing id re-runs the posture.
          It animates whatever fills the frame: generated body,
          real art, AND the worn Ascent, so nothing floats. */}
      <div
        key={swing?.id || 0}
        style={{ position: "absolute", inset: 0,
          animation: swing ? strikeAnim : "none",
          transformOrigin: "50% 80%" }}
      >
        {/* real art layer — invisible until it genuinely loads */}
        {artSrc && !artDead && (
          <img
            key={artSrc}
            src={artSrc}
            alt="being"
            onLoad={() => setArtLive(true)}
            onError={() => setArtDead(true)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
              imageRendering: "pixelated", objectFit: "contain",
              opacity: artLive ? 1 : 0, zIndex: 2, pointerEvents: "none" }}
          />
        )}
        {/* generated body — steps aside the day real art lands */}
        {!artLive && <Body p={p} walkFrame={walkFrame} isIdle={isIdle} />}
        {/* the Ascent, worn visibly */}
        <TierAccents tier={tier} glow={p.glow} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TERROR TRAVELER — THE REVERSED COMPANION.
//
// In Terror TCG, the human plays the cards and the beast walks
// beside them. In Annihilation, before any of them lost, before
// any of them were cards — the beast walks ahead, and the small
// human follows, a single glowing card in hand.
//
// PLACEHOLDER for the cross-game character import (Terror first,
// other IE games later — configured in a future batch). Art
// resolution, two strikes:
//   1) the real Terror player sprite (same manifest paths the
//      Plaza human uses) — if the CDN ever serves it, it appears
//   2) the generated traveler — head, tunic, legs, the card
// ─────────────────────────────────────────────────────────────
export function TerrorTravelerVisual({ gender = "m", facing = { x: 0, y: 1 }, walkFrame = false, isIdle = false, tone = "#c9a84c" }) {
  const src = isIdle
    ? getIdleSprite(gender, false)
    : getPlayerSprite(gender, facing?.x ?? 0, facing?.y ?? 1, walkFrame);
  const [err, setErr] = useState(false);
  const prevSrc = useRef(src);
  useEffect(() => {
    if (prevSrc.current !== src) { prevSrc.current = src; setErr(false); }
  }, [src]);

  if (src && !err) {
    return (
      <img key={src} src={src} alt="traveler" onError={() => setErr(true)}
        style={{ width: "100%", height: "100%", imageRendering: "pixelated",
          filter: `drop-shadow(0 0 6px ${tone}44)` }} />
    );
  }

  // Generated traveler — the same figure that walked the Old Fold,
  // now walking behind you. Head, tunic, legs, shadow… and the card.
  const bob = walkFrame ? "-3%" : "0%";
  return (
    <div style={{ position: "relative", width: "100%", height: "100%",
      transform: `translateY(${bob})`, transition: "transform 0.12s" }}>
      <div style={{ position: "absolute", bottom: 0, left: "24%", width: "52%", height: "8%",
        borderRadius: "50%", background: "rgba(0,0,0,0.45)", filter: "blur(1px)" }} />
      <div style={{ position: "absolute", bottom: "6%", left: "34%", width: "12%", height: "20%",
        background: "#241c12", borderRadius: 2 }} />
      <div style={{ position: "absolute", bottom: "6%", right: "34%", width: "12%", height: "20%",
        background: "#241c12", borderRadius: 2 }} />
      <div style={{ position: "absolute", bottom: "22%", left: "26%", width: "48%", height: "36%",
        background: `linear-gradient(180deg, ${tone}, ${tone}99)`,
        borderRadius: "4px 4px 2px 2px",
        boxShadow: "inset -2px -3px 4px rgba(0,0,0,0.35)" }} />
      <div style={{ position: "absolute", bottom: "52%", left: "50%", transform: "translateX(-50%)",
        width: "36%", height: "32%", borderRadius: "50%",
        background: "linear-gradient(145deg, #d8b890, #b89468)",
        boxShadow: "inset -2px -2px 3px rgba(0,0,0,0.3)" }}>
        <div style={{ position: "absolute", top: "-24%", left: "8%", width: "84%", height: "52%",
          borderRadius: "50% 50% 20% 20%", background: "#2e2418" }} />
        <div style={{ position: "absolute", top: "46%", left: "26%", width: "9%", height: "9%",
          minWidth: 2, minHeight: 2, borderRadius: "50%", background: "#241a10" }} />
        <div style={{ position: "absolute", top: "46%", right: "26%", width: "9%", height: "9%",
          minWidth: 2, minHeight: 2, borderRadius: "50%", background: "#241a10" }} />
      </div>
      {/* the card — a Terror hand of one, faintly glowing */}
      <div style={{ position: "absolute", bottom: "30%", right: "14%", width: "16%", height: "22%",
        borderRadius: 2, background: "linear-gradient(160deg, #1a1030, #2a1a48)",
        border: `1px solid ${tone}aa`, boxShadow: `0 0 6px ${tone}66`,
        animation: "anniCard 2.4s ease-in-out infinite" }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ANNIHILATION PLAYER — the one mount every zone uses.
//
// Wraps engine/PlayerEntity (positioning math lives THERE, once)
// and fills it with the being body + the reversed companion.
// Zones pass exactly what they passed before, plus `character`.
//
//   <AnnihilationPlayer
//     character={character}
//     pos={ctrl.pos} facing={ctrl.facing}
//     walkFrame={anim.walkFrame} isIdle={anim.isIdle}
//     idleFrame={anim.idleFrame}
//     tile={TILE}
//     opacity={...}
//   />
//
// showCompanion defaults TRUE — the traveler follows you into the
// field. (Dungeons included: they walked in behind you. They
// always do.) Pass showCompanion={false} to override.
// ─────────────────────────────────────────────────────────────
export function AnnihilationPlayer({
  character,
  pos,
  facing = { x: 0, y: 1 },
  walkFrame = false,
  isIdle = false,
  idleFrame = false,
  tile = 48,
  opacity = 1,
  showCompanion = true,
  equipped = null,
  swing = null, // ctrl.lastSwing — the strike posture trigger
}) {
  const beingId = character?.beingId || null;
  const { level } = levelForXp(character?.xp || 0);
  const tier = tierForLevel(level);

  return (
    <PlayerEntity
      pos={pos}
      facing={facing}
      walkFrame={walkFrame}
      isIdle={isIdle}
      idleFrame={idleFrame}
      tile={tile}
      opacity={opacity}
      showCompanion={showCompanion}
      equipped={equipped}
      playerNode={
        <BeingSprite
          beingId={beingId}
          facing={facing}
          walkFrame={walkFrame}
          isIdle={isIdle}
          tier={tier}
          swing={swing}
        />
      }
      companionNode={
        <TerrorTravelerVisual
          facing={facing}
          walkFrame={walkFrame}
          isIdle={isIdle}
        />
      }
    />
  );
}
